// DeviceDetails.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import axios from "axios";
import config from "../../config.js";
import Upload_Icon from "../../images/vehicle/Upload_icon.svg";
import Upload_Edit_Icon from "../../images/vehicle/Upload_Edit_icon.svg";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import { getDeviceTypes } from "./deviceApi";

const formatTypeLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bGps\b/g, "GPS")
    .replace(/\bNfc\b/g, "NFC")
    .replace(/\bRfid\b/g, "RFID")
    .replace(/\bTv\b/g, "TV")
    .replace(/\bCctv\b/g, "CCTV");

const normalizeTypeOption = (type) => {
  const value =
    typeof type === "string"
      ? type
      : type?.name || type?.value || type?.sensorType || type?.type || "";
  if (!value) return null;
  return {
    id: typeof type === "object" ? type.id : undefined,
    value,
    label:
      typeof type === "object"
        ? type.label || type.displayName || formatTypeLabel(value)
        : formatTypeLabel(value),
  };
};

const uniqueOptions = (options) => {
  const seen = new Set();
  return options.filter((option) => {
    if (!option?.value || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

const extractSensorTypesFromDeviceTypes = (deviceTypes) =>
  uniqueOptions(
    deviceTypes.flatMap((type) => {
      const sensorSources = [
        type?.sensorTypes,
        type?.sensorTypeOptions,
        type?.supportedSensorTypes,
        type?.sensors,
      ].filter(Boolean);

      return sensorSources.flatMap((source) => {
        const values = Array.isArray(source)
          ? source
          : typeof source === "string"
            ? source.split(",")
            : [];
        return values.map((value) => normalizeTypeOption(value)).filter(Boolean);
      });
    }),
  );

const SENSOR_TYPE_OPTIONS = [
  "HUMIDITY",
  "TEMPERATURE",
  "FUEL",
  "VIBRATION",
  "SMOKE",
  "PRESSURE",
  "BATTERY_VOLTAGE",
  "IGNITION",
].map((value) => ({
  value,
  label: formatTypeLabel(value),
}));

export default function DeviceDetails({
  formData,
  handleInputChange,
  handleImageUpload,
  handleRemoveImage,
  RequiredLabel,
  StandardLabel,
  errors = {},
  handleAddSpecification, // ← add this
  handleRemoveSpecification, // ← add this
  handleSpecificationChange,
  hideCloseButton = false,
  onClose,
  isEditMode,
  isReadOnly = false,
  hideIrisideaOwner = false,
  allowedCustomerOrganizationId = null,
  fixedOwnerOrganizationName = "",
  lockOwnerToOrganization = false,
  disableInstallationStatus = false,
  // forceOtherOwnerName = null,
  t,
}) {
  const [organizations, setOrganizations] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [isDeviceTypesLoading, setIsDeviceTypesLoading] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/organization`);
        setOrganizations(response?.data?.organizations || []);
      } catch (error) {
        console.log("Error fetching owned by organizations:", error);
        setOrganizations([]);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchDeviceTypes = async () => {
      setIsDeviceTypesLoading(true);
      try {
        const types = await getDeviceTypes();
        if (isMounted) {
          setDeviceTypes(types.filter((type) => !type?.isDeleted));
        }
      } catch (error) {
        console.log("Error fetching device types:", error);
        if (isMounted) {
          setDeviceTypes([]);
        }
      } finally {
        if (isMounted) {
          setIsDeviceTypesLoading(false);
        }
      }
    };

    fetchDeviceTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  const ownerType = useMemo(() => {
    if (formData.ownedByType === "Other") return "Irisidea";
    if (formData.ownedByType) return formData.ownedByType;
    if (!formData.ownedBy || formData.ownedBy === "Irisidea") return "Irisidea";
    return organizations.some((org) => org.name === formData.ownedBy)
      ? "Customer"
      : "Irisidea";
  }, [formData.ownedBy, formData.ownedByType, organizations]);

  const selectedOwnerOrganization = useMemo(() => {
    if (formData.ownedByOrganizationId)
      return String(formData.ownedByOrganizationId);
    const matchedOrg = organizations.find(
      (org) => org.name === formData.ownedBy,
    );
    return matchedOrg ? String(matchedOrg.id) : "";
  }, [formData.ownedBy, formData.ownedByOrganizationId, organizations]);

  const isInstallationStatusReadOnly =
    isReadOnly || disableInstallationStatus;

  const deviceTypeOptions = useMemo(() => {
    const apiOptions = uniqueOptions(
      deviceTypes.map((type) => normalizeTypeOption(type)).filter(Boolean),
    );

    if (
      formData.deviceType &&
      !apiOptions.some((option) => option.value === formData.deviceType)
    ) {
      return [
        ...apiOptions,
        {
          value: formData.deviceType,
          label: formatTypeLabel(formData.deviceType),
        },
      ];
    }

    return apiOptions;
  }, [deviceTypes, formData.deviceType]);

  const sensorTypeOptions = useMemo(() => {
    const apiOptions = SENSOR_TYPE_OPTIONS;

    if (
      formData.sensorType &&
      !apiOptions.some((option) => option.value === formData.sensorType)
    ) {
      return [
        ...apiOptions,
        {
          value: formData.sensorType,
          label: formatTypeLabel(formData.sensorType),
        },
      ];
    }

    return apiOptions;
  }, [deviceTypes, formData.sensorType]);

  // Owned By "Other" is disabled for create/update. Keep the previous logic
  // commented so it can be restored if the free-text owner flow returns.
  // const otherOwnerValue =
  //   forceOtherOwnerName ||
  //   (formData.ownedByOther ??
  //     (ownerType === "Other" ? formData.ownedBy || "" : ""));

  const selectableOrganizations = useMemo(() => {
    if (!allowedCustomerOrganizationId) return organizations;
    return organizations.filter(
      (org) => String(org.id) === String(allowedCustomerOrganizationId),
    );
  }, [allowedCustomerOrganizationId, organizations]);

  const updateOwnerField = (field, value) => {
    handleInputChange({ target: { value } }, field);
  };

  const handleOwnerTypeChange = (event) => {
    const value = event.target.value;
    if (value === "Other") return;
    updateOwnerField("ownedByType", value);

    if (value === "Irisidea") {
      updateOwnerField("ownedBy", "Irisidea");
      updateOwnerField("ownedByOrganizationId", "");
      updateOwnerField("ownedByOther", "");
      return;
    }

    // Owned By "Other" / external vendor flow disabled for create/update.
    // if (forceOtherOwnerName && value === "Other") {
    //   updateOwnerField("ownedBy", forceOtherOwnerName);
    //   updateOwnerField("ownedByOrganizationId", "");
    //   updateOwnerField("ownedByOther", forceOtherOwnerName);
    //   return;
    // }

    updateOwnerField("ownedBy", "");
    updateOwnerField("ownedByOrganizationId", "");
    updateOwnerField("ownedByOther", "");
  };

  const handleOwnerOrganizationChange = (event) => {
    const organizationId = event.target.value;
    const selectedOrganization = organizations.find(
      (org) => String(org.id) === String(organizationId),
    );

    updateOwnerField("ownedByOrganizationId", organizationId);
    updateOwnerField("ownedBy", selectedOrganization?.name || "");
  };

  // Owned By "Other" change handler disabled with the option.
  // const handleOtherOwnerChange = (event) => {
  //   if (forceOtherOwnerName) return;
  //   const value = event.target.value;
  //   updateOwnerField("ownedByOther", value);
  //   updateOwnerField("ownedBy", value);
  // };

  const ownerSelectStyles = {
    height: "55px",
    borderRadius: "16px",
    padding: 0,
    border: "2px solid #183059",
    "&:hover": { border: "2px solid #183059" },
    "&.Mui-focused": { border: "2px solid #183059" },
    "& fieldset": { border: "none" },
    "& .MuiSelect-select": {
      padding: "16px 24px",
    },
  };
  const ownerTextFieldStyles = {
    "& .MuiOutlinedInput-root": {
      height: "55px",
      borderRadius: "16px",
      padding: "16px 24px",
      border: "2px solid #183059",
      backgroundColor: "#F4F6F9",
      "& fieldset": {
        border: "none !important",
      },
    },
    "& .MuiInputBase-input": {
      padding: 0,
      color: "#183059",
      WebkitTextFillColor: "#183059",
    },
  };

  const hasDeviceImeiNumber = [
    "GPS_TRACKER",
    "DASH_CAMERA",
    "CABIN_CAMERA",
    "TABLET",
    "TV",
    "MICROPHONE",
    "DONGLE",
    "MOBILE_PHONE",
    "DRIVER_DASHBOARD",
  ].includes(formData.deviceType);
  const visibleDeviceDetailFieldCount =
    7 +
    (formData.deviceType === "SENSOR" ? 4 : 0) +
    (hasDeviceImeiNumber ? 1 : 0);
  const deviceDetailRowCount = Math.ceil(visibleDeviceDetailFieldCount / 2);
  const uploadBoxHeight = deviceDetailRowCount * 72 + (deviceDetailRowCount - 1) * 16;

  return (
    <Box
      sx={{
        padding: { xs: "20px", md: "40px" },
        margin: { xs: '0px 20px', md: '0px 40px' },
        backgroundColor: isEditMode ? "#E8EAEE" : "#FFFFFF",
        // border: '1px solid #A5DCF3',
        borderRadius: "10px",
        // boxShadow: '-2px 2px 5px 0px #00000040',
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Paper
          sx={{
            height: 52,
            width: "100%",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            color="white"
            sx={{ fontSize: { xs: "18px", md: "20px" } }}
          >
            {t("Device Details")}
          </Typography>
        </Paper>
        {!hideCloseButton && (
          <Button
            onClick={onClose}
            sx={{
              width: 44,
              height: 44,
              minWidth: 44,
              ml: 2,
              borderRadius: "24px",
              p: "12px",
              bgcolor: "#EE3B2B",
              color: "#fff",
              boxShadow: "-2px 2px 5px rgba(0, 0, 0, 0.25)",
              "&:hover": {
                bgcolor: "#d93425",
              },
            }}
          >
            X
          </Button>
        )}
      </Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} mb={1.5}>
                <RequiredLabel label={t("Owned By")} />
                <FormControl fullWidth error={!!errors.ownedBy}>
                  <Select
                    value={hideIrisideaOwner ? "Irisidea" : ownerType}
                    onChange={handleOwnerTypeChange}
                    disabled={
                      isReadOnly || lockOwnerToOrganization || hideIrisideaOwner
                    }
                    sx={ownerSelectStyles}
                  >
                    <MenuItem value="Irisidea">{t("Irisidea")}</MenuItem>
                    {!hideIrisideaOwner && (
                      <MenuItem value="Customer">{t("Customers")}</MenuItem>
                    )}
                  </Select>
                  {errors.ownedBy && (
                    <FormHelperText>{errors.ownedBy}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {!lockOwnerToOrganization && ownerType === "Customer" && (
                <Grid item xs={12} sm={6} mb={1.5}>
                  <RequiredLabel label={t("Customer Organization")} />
                  <FormControl fullWidth error={!!errors.ownedBy}>
                    <Select
                      value={selectedOwnerOrganization}
                      onChange={handleOwnerOrganizationChange}
                      disabled={isReadOnly}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span style={{ color: "#C8CBD0" }}>
                              {t("Select Organization")}
                            </span>
                          );
                        }

                        const selectedOrganization = organizations.find(
                          (org) => String(org.id) === String(selected),
                        );

                        return selectedOrganization?.name || selected;
                      }}
                      sx={ownerSelectStyles}
                    >
                      {selectableOrganizations.map((org) => (
                        <MenuItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.ownedBy && (
                      <FormHelperText>{errors.ownedBy}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
              {/*
              Owned By "Other" free-text field disabled with the option.
              {ownerType === "Other" && (
                <Grid item xs={12} sm={6} mb={1.5}>
                  <RequiredLabel label={t("Owner Name")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Owner Name")}
                    value={otherOwnerValue}
                    onChange={handleOtherOwnerChange}
                    disabled={isReadOnly || Boolean(forceOtherOwnerName)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "55px",
                        borderRadius: "16px",
                        padding: "16px 24px",
                        border: "2px solid #183059",
                        "&:hover": {
                          border: "2px solid #183059",
                        },
                        "&.Mui-focused": {
                          border: "2px solid #183059",
                        },
                        "& fieldset": {
                          border: "none !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                      },
                    }}
                  />
                </Grid>
              )}
              */}
              <Grid item xs={12} sm={6} mb={1.5}>
                <RequiredLabel label={t("Device Type")} />
                <FormControl fullWidth error={!!errors.deviceType}>
                  <Select
                    value={formData.deviceType || ""}
                    onChange={(e) => handleInputChange(e, "deviceType")}
                    disabled={isReadOnly}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#C8CBD0" }}>
                            {t("Select Device Type")}
                          </span>
                        );
                      }

                      const selectedOption = deviceTypeOptions.find(
                        (option) => option.value === selected,
                      );

                      return selectedOption
                        ? t(selectedOption.label)
                        : selected;
                    }}
                    sx={{
                      height: "55px",
                      borderRadius: "16px",
                      padding: 0,
                      border: errors.deviceType
                        ? "2px solid #d32f2f"
                        : "2px solid #183059",
                      "&:hover": {
                        border: errors.deviceType
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                      },
                      "&.Mui-focused": {
                        border: errors.deviceType
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                      },
                      "& fieldset": { border: "none" },
                      "& .MuiSelect-select": {
                        padding: "16px 24px",
                      },
                    }}
                  >
                    {isDeviceTypesLoading ? (
                      <MenuItem value="" disabled>
                        {t("Loading device types...")}
                      </MenuItem>
                    ) : null}
                    {!isDeviceTypesLoading && deviceTypeOptions.length === 0 ? (
                      <MenuItem value="" disabled>
                        {t("No Device Types Found")}
                      </MenuItem>
                    ) : null}
                    {deviceTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.label)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.deviceType && (
                    <FormHelperText>{errors.deviceType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {formData.deviceType === "SENSOR" && (
                <Grid item xs={12} sm={6} mb={1.5}>
                  <RequiredLabel label={t("Sensor Type")} />
                  <FormControl fullWidth error={!!errors.sensorType}>
                    {sensorTypeOptions.length ? (
                      <Select
                      value={formData.sensorType || ""}
                      onChange={(e) => handleInputChange(e, "sensorType")}
                      disabled={isReadOnly}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span style={{ color: "#C8CBD0" }}>
                              {t("Select Sensor Type")}
                            </span>
                          );
                        }

                        const selectedOption = sensorTypeOptions.find(
                          (option) => option.value === selected,
                        );

                        return selectedOption
                          ? t(selectedOption.label)
                          : selected;
                      }}
                      sx={{
                        height: "55px",
                        borderRadius: "16px",
                        padding: "0 24px",
                        border: errors.sensorType
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                        "&:hover": {
                          border: errors.sensorType
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                        },
                        "&.Mui-focused": {
                          border: errors.sensorType
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                        },
                        "& fieldset": { border: "none" },
                      }}
                    >
                        {sensorTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                      </Select>
                    ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        placeholder={t("Type Sensor Type")}
                        value={formData.sensorType || ""}
                        onChange={(e) => handleInputChange(e, "sensorType")}
                        disabled={isReadOnly}
                        error={!!errors.sensorType}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "55px",
                            borderRadius: "16px",
                            padding: "16px 24px",
                            border: errors.sensorType
                              ? "2px solid #d32f2f"
                              : "2px solid #183059",
                            "&:hover": {
                              border: errors.sensorType
                                ? "2px solid #d32f2f"
                                : "2px solid #183059",
                            },
                            "&.Mui-focused": {
                              border: errors.sensorType
                                ? "2px solid #d32f2f"
                                : "2px solid #183059",
                            },
                            "& fieldset": { border: "none !important" },
                          },
                          "& .MuiInputBase-input": {
                            padding: 0,
                          },
                        }}
                      />
                    )}
                    {errors.sensorType && (
                      <FormHelperText>{errors.sensorType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
              {formData.deviceType === "SENSOR" && (
                <>
                  <Grid item xs={12} sm={6} mb={1.5}>
                    <StandardLabel label={t("Unit")} />
                    <TextField
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      placeholder={t("Type Unit")}
                      value={formData.unit || ""}
                      onChange={(e) => handleInputChange(e, "unit")}
                      disabled={false}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "55px",
                          borderRadius: "16px",
                          padding: "16px 24px",
                          border: "2px solid #183059",
                          "&:hover": { border: "2px solid #183059" },
                          "&.Mui-focused": { border: "2px solid #183059" },
                          "& fieldset": { border: "none !important" },
                        },
                        "& .MuiInputBase-input": { padding: 0 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} mb={1.5}>
                    <StandardLabel label={t("Min Threshold")} />
                    <TextField
                      fullWidth
                      type="number"
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      placeholder={t("Type Min Threshold")}
                      value={formData.minValue ?? ""}
                      onChange={(e) => handleInputChange(e, "minValue")}
                      disabled={false}
                      error={!!errors.minValue}
                      helperText={errors.minValue}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "55px",
                          borderRadius: "16px",
                          padding: "16px 24px",
                          border: errors.minValue
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                          "&:hover": {
                            border: errors.minValue
                              ? "2px solid #d32f2f"
                              : "2px solid #183059",
                          },
                          "&.Mui-focused": {
                            border: errors.minValue
                              ? "2px solid #d32f2f"
                              : "2px solid #183059",
                          },
                          "& fieldset": { border: "none !important" },
                        },
                        "& .MuiInputBase-input": { padding: 0 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} mb={1.5}>
                    <StandardLabel label={t("Max Threshold")} />
                    <TextField
                      fullWidth
                      type="number"
                      variant="outlined"
                      InputLabelProps={{ shrink: false }}
                      placeholder={t("Type Max Threshold")}
                      value={formData.maxValue ?? ""}
                      onChange={(e) => handleInputChange(e, "maxValue")}
                      disabled={false}
                      error={!!errors.maxValue}
                      helperText={errors.maxValue}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "55px",
                          borderRadius: "16px",
                          padding: "16px 24px",
                          border: errors.maxValue
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                          "&:hover": {
                            border: errors.maxValue
                              ? "2px solid #d32f2f"
                              : "2px solid #183059",
                          },
                          "&.Mui-focused": {
                            border: errors.maxValue
                              ? "2px solid #d32f2f"
                              : "2px solid #183059",
                          },
                          "& fieldset": { border: "none !important" },
                        },
                        "& .MuiInputBase-input": { padding: 0 },
                      }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6} mb={0}>
                <RequiredLabel label={t("Device ID")} />
                <TextField
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  placeholder={t("typedeviceId")}
                  value={formData.deviceId || ""}
                  onChange={(e) => handleInputChange(e, "deviceId")}
                  disabled={isReadOnly}
                  error={!!errors.deviceId}
                  helperText={errors.deviceId}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "55px",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      border: errors.deviceId
                        ? "2px solid #d32f2f"
                        : "2px solid #183059",
                      "&:hover": {
                        border: errors.deviceId
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                      },
                      "&.Mui-focused": {
                        border: errors.deviceId
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                      },
                      "& fieldset": {
                        border: "none !important",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                    },
                  }}
                />
              </Grid>
              {hasDeviceImeiNumber && (
                <Grid item xs={12} sm={6} mb={0}>
                  <StandardLabel label={t("Device IMEI Number")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("Type Device IMEI Number")}
                    value={formData.imeiNumber || ""}
                    onChange={(e) => handleInputChange(e, "imeiNumber")}
                    disabled={isReadOnly}
                    error={!!errors.imeiNumber}
                    helperText={errors.imeiNumber}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "55px",
                        borderRadius: "16px",
                        padding: "16px 24px",
                        border: errors.imeiNumber
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                        "&:hover": {
                          border: errors.imeiNumber
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                        },
                        "&.Mui-focused": {
                          border: errors.imeiNumber
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                        },
                        "& fieldset": {
                          border: "none !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} mt={2} mb={0}>
                <StandardLabel label={t("Model")} />
                <TextField
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  placeholder={t("Type Model ")}
                  value={formData.model || ""}
                  onChange={(e) => handleInputChange(e, "model")}
                  disabled={isReadOnly}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "55px",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      border: "2px solid #183059",
                      "&:hover": {
                        border: "2px solid #183059",
                      },
                      "&.Mui-focused": {
                        border: "2px solid #183059",
                      },
                      "& fieldset": {
                        border: "none !important",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} mt={2} mb={0}>
                <StandardLabel label={t("Vendor/Brand")} />
                <TextField
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  placeholder={t("Type Vendor/Brand")}
                  value={formData.vendorBrand || ""}
                  onChange={(e) => handleInputChange(e, "vendorBrand")}
                  disabled={isReadOnly}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "55px",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      border: "2px solid #183059",
                      "&:hover": {
                        border: "2px solid #183059",
                      },
                      "&.Mui-focused": {
                        border: "2px solid #183059",
                      },
                      "& fieldset": {
                        border: "none !important",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} mt={2} mb={0}>
                <RequiredLabel label={t("Working Condition")} />
                <FormControl fullWidth error={!!errors.workingCondition}>
                  <Box
                    sx={{
                      display: "flex",
                      height: "44px",
                      width: "fit-content",
                      borderRadius: "22px",
                      backgroundColor: "#D9DDE3",
                      padding: "4px",
                      gap: "4px",
                    }}
                  >
                    <Box
                      onClick={() =>
                        !isReadOnly &&
                        handleInputChange(
                          { target: { value: "WORKING" } },
                          "workingCondition",
                        )
                      }
                      sx={{
                        px: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "18px",
                        cursor: "pointer",
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontSize: "14px",
                        transition: "all 0.2s ease",
                        backgroundColor:
                          formData.workingCondition === "WORKING"
                            ? "#008C23"
                            : "transparent",
                        color:
                          formData.workingCondition === "WORKING"
                            ? "#FFFFFF"
                            : "#1F242E",
                      }}
                    >
                      {t("Working")}
                    </Box>
                    <Box
                      onClick={() =>
                        !isReadOnly &&
                        handleInputChange(
                          { target: { value: "NOT_WORKING" } },
                          "workingCondition",
                        )
                      }
                      sx={{
                        px: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "18px",
                        cursor: "pointer",
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontSize: "14px",
                        transition: "all 0.2s ease",
                        backgroundColor:
                          formData.workingCondition === "NOT_WORKING"
                            ? "#EE3B2B"
                            : "transparent",
                        color:
                          formData.workingCondition === "NOT_WORKING"
                            ? "#FFFFFF"
                            : "#1F242E",
                      }}
                    >
                      {t("Not Working")}
                    </Box>
                  </Box>
                  {errors.workingCondition && (
                    <FormHelperText>{errors.workingCondition}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              width: "100%",
              border: errors.deviceImage
                ? "2px dashed #d32f2f"
                : "2px dashed #183059",
              borderRadius: 2,
              height: { xs: 260, md: uploadBoxHeight },
              backgroundColor: "#E8EAEE",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {formData.deviceImagePreview ? (
              <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                <img
                  src={formData.deviceImagePreview}
                  alt="Device"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                  }}
                  size="small"
                >
                  <img
                    src={Upload_Edit_Icon}
                    alt="Remove"
                    style={{ filter: "invert(1)" }}
                  />
                </IconButton>
              </Box>
            ) : (
              <>
                <img
                  src={Upload_Icon}
                  alt="Upload"
                  style={{ width: 50, height: 50 }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    color: "#1F242E",
                    fontFamily: "Roboto",
                    fontWeight: 600,
                    fontSize: "20px",
                    lineHeight: "100%",
                    textAlign: "center",
                  }}
                >
                  {t("Device Image")}
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "pointer",
                  }}
                />
              </>
            )}
          </Box>
          {errors.deviceImage && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              {errors.deviceImage}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} sm={3} mb={0}>
          <RequiredLabel label={t("Installation Status")} />
          <FormControl fullWidth error={!!errors.status}>
            <Box
              sx={{
                display: "flex",
                height: "44px",
                width: "fit-content",
                borderRadius: "22px",
                backgroundColor: "#D9DDE3",
                padding: "4px",
                gap: "4px",
              }}
            >
              <Box
                onClick={() =>
                  !isInstallationStatusReadOnly &&
                  handleInputChange(
                    { target: { value: "INSTALLED" } },
                    "status",
                  )
                }
                sx={{
                  px: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "18px",
                  cursor: isInstallationStatusReadOnly ? "not-allowed" : "pointer",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "14px",
                  transition: "all 0.2s ease",
                  backgroundColor:
                    formData.status === "INSTALLED" ? "#008C23" : "transparent",
                  color:
                    formData.status === "INSTALLED" ? "#FFFFFF" : "#1F242E",
                }}
              >
                {t("Installed")}
              </Box>
              <Box
                onClick={() =>
                  !isInstallationStatusReadOnly &&
                  handleInputChange({ target: { value: "PENDING" } }, "status")
                }
                sx={{
                  px: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "18px",
                  cursor: isInstallationStatusReadOnly ? "not-allowed" : "pointer",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "14px",
                  transition: "all 0.2s ease",
                  backgroundColor:
                    formData.status === "PENDING" ? "#EE3B2B" : "transparent",
                  color: formData.status === "PENDING" ? "#FFFFFF" : "#1F242E",
                }}
              >
                {t("Not Installed")}
              </Box>
            </Box>
            {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={9} mb={1.5}>
          <StandardLabel label={t("Device Remarks")} />
          <TextField
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: false }}
            placeholder={t("Type Device Remarks")}
            value={formData.remarks || ""}
            onChange={(e) => handleInputChange(e, "remarks")}
            disabled={isReadOnly || disableInstallationStatus}
            multiline
            rows={2}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                padding: "16px 24px",
                border: "2px solid #183059",
                "&:hover": {
                  border: "2px solid #183059",
                },
                "&.Mui-focused": {
                  border: "2px solid #183059",
                },
                "& fieldset": {
                  border: "none !important",
                },
              },
              "& .MuiInputBase-input": {
                padding: 0,
              },
            }}
          />
        </Grid>
      </Grid>
      <Paper
        sx={{
          height: 52,
          width: "100%",
          borderRadius: "8px",
          padding: "12px 24px",
          backgroundColor: "#647490",
          boxShadow: "-2px 2px 5px 0px #00000040",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          color="white"
          sx={{ fontSize: { xs: "18px", md: "20px" } }}
        >
          {t("Specifications")}
        </Typography>
      </Paper>
      <Grid container spacing={2} mt={1} sx={{ padding: "12px 24px" }}>
        {/* Specifications */}
        <Grid container spacing={2}>
          <Grid item xs={12} >
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
           
              }}
            >
                <Grid item xs={6} mb={1.5}>
              <StandardLabel label={t("Name")} /></Grid>
              <Grid item xs={6} mb={1.5}>
                <StandardLabel label={t("Value")} />
              </Grid>
            </Box>
            {(formData.specifications || []).map((specification) => (
              <Box
                key={specification.id}
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 1.5,
                  alignItems: "flex-start",
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t("Type specification name")}
                  value={specification.name || ""}
                  onChange={(e) =>
                    !isReadOnly &&
                    handleSpecificationChange(
                      specification.id,
                      "name",
                      e.target.value,
                    )
                  }
                  error={!!errors[`specification-${specification.id}`]}
                  helperText={errors[`specification-${specification.id}`]}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "55px",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      border: errors[`specification-${specification.id}`]
                        ? "2px solid #d32f2f"
                        : "2px solid #183059",
                      "&:hover": {
                        border: errors[`specification-${specification.id}`]
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                      },
                      "&.Mui-focused": {
                        border: errors[`specification-${specification.id}`]
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                      },
                      "& fieldset": { border: "none !important" },
                    },
                    "& .MuiInputBase-input": { padding: 0 },
                  }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t("Type specification value")}
                  value={specification.value || ""}
                  onChange={(e) =>
                    !isReadOnly &&
                    handleSpecificationChange(
                      specification.id,
                      "value",
                      e.target.value,
                    )
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "55px",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      border: "2px solid #183059",
                      "&:hover": { border: "2px solid #183059" },
                      "&.Mui-focused": { border: "2px solid #183059" },
                      "& fieldset": { border: "none !important" },
                    },
                    "& .MuiInputBase-input": { padding: 0 },
                  }}
                />
                <Button
                  onClick={() => handleRemoveSpecification(specification.id)}
                  disabled={isReadOnly}
                  sx={{
                    mt: 1,
                    color: "#EE3B2B",
                    textTransform: "none",
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "14px",
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  {t("Delete")}
                </Button>
              </Box>
            ))}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                onClick={handleAddSpecification}
                disabled={isReadOnly}
                size="small"
                sx={{
                  width: "97px",
                  height: "36px",
                  gap: "10px",
                  px: "16px", // padding-left & padding-right
                  py: "8px", // padding-top & padding-bottom
                  borderRadius: "8px",

                  bgcolor: "#20A8E0",
                  color: "#FFFFFF",

                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "16px",
                
                  letterSpacing: 0,

                  textTransform: "none",

                  "&:hover": {
                    bgcolor: "#1b96c9",
                  },
                }}
              >
                {t("Add")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
